/** @jsxImportSource @emotion/react */
import React, { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import { downloadCSV } from "@phoenix/utils/csvUtils";

const tableContainerCSS = css`
  background: var(--ac-global-background-color-white);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
`;

const scrollableWrapperCSS = css`
  max-height: 400px;
  overflow-y: auto;
  
  /* Scrollbar styles for pure aesthetics */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--ac-global-color-grey-300);
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--ac-global-color-grey-400);
  }
`;

const tableHeaderCSS = css`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: var(--ac-global-color-grey-50);
  border-bottom: 1px solid var(--ac-global-color-grey-200);
`;

const titleCSS = css`
  font-size: 15px;
  font-weight: 700;
  color: var(--ac-global-text-color-900);
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const buttonCSS = css`
  background: white;
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ac-global-text-color-600);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  &:hover {
    background: var(--ac-global-color-grey-50);
    color: var(--ac-global-text-color-900);
    border-color: var(--ac-global-color-grey-300);
  }
`;

const tableCSS = css`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
`;

const thCSS = css`
  padding: 12px 24px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ac-global-text-color-400);
  border-bottom: 1px solid var(--ac-global-color-grey-100);
`;

const tdCSS = css`
  padding: 14px 24px;
  font-size: 13px;
  color: var(--ac-global-text-color-700);
  border-bottom: 1px solid var(--ac-global-color-grey-50);
`;

const toolRowCSS = css`
  &:hover {
    background: var(--ac-global-color-grey-50);
  }
  &:last-child td {
    border-bottom: none;
  }
`;

const dotCSS = (color: string) => css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${color};
  display: inline-block;
  margin-right: 12px;
`;

const valueCSS = css`
  font-weight: 600;
  color: var(--ac-global-text-color-900);
`;

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4"];

const getPreciseMs = (iso: string) => {
  if (!iso) return 0;
  const date = new Date(iso);
  const match = iso.match(/\.(\d+)/);
  if (match) {
    const seconds = Math.floor(date.getTime() / 1000);
    const msFrac = parseFloat("0." + match[1]) * 1000;
    return seconds * 1000 + msFrac;
  }
  return date.getTime();
};

export function ToolPerformanceTable({
  projectId,
  timeRange,
  fetchKey,
  onDataExport,
}: ProjectMetricViewProps) {
  const data = useLazyLoadQuery<any>(
    graphql`
      query ToolPerformanceTableQuery($projectId: ID!, $timeRange: TimeRange!, $filterCondition: String!) {
        project: node(id: $projectId) {
          ... on Project {
            spans(timeRange: $timeRange, filterCondition: $filterCondition, first: 10000) {
              edges {
                node {
                  name
                  agentName
                  latencyMs
                  statusCode
                  startTime
                  endTime
                }
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
      filterCondition: 'span_kind == "TOOL"',
    },
    { fetchPolicy: "network-only", fetchKey }
  );

  const tools = useMemo(() => {
    const edges = data.project?.spans?.edges ?? [];
    const toolMap: Record<string, { name: string; agent: string; count: number; failed: number; latencies: number[] }> = {};

    edges.forEach(({ node }: any) => {
      const name = node.name || "unknown";
      const agent = node.agentName || "System";
      const key = `${name}|${agent}`;

      if (!toolMap[key]) {
        toolMap[key] = { name, agent, count: 0, failed: 0, latencies: [] };
      }
      toolMap[key].count++;
      if (node.statusCode === "ERROR") {
        toolMap[key].failed++;
      }

      // Calculate exact latency from timestamps for sub-millisecond precision
      if (node.startTime && node.endTime) {
        const duration = getPreciseMs(node.endTime) - getPreciseMs(node.startTime);
        toolMap[key].latencies.push(duration);
      } else if (node.latencyMs != null) {
        toolMap[key].latencies.push(node.latencyMs);
      }
    });

    return Object.values(toolMap).map((data) => {
      const avgLatency = data.latencies.length > 0
        ? data.latencies.reduce((a, b) => a + b, 0) / data.latencies.length
        : 0;
      const successRate = ((data.count - data.failed) / data.count) * 100;
      return {
        key: `${data.name}|${data.agent}`,
        name: data.name,
        agent: data.agent,
        count: data.count,
        failed: data.failed,
        successRate,
        avgLatency,
      };
    }).sort((a, b) => b.count - a.count);
  }, [data]);

  useEffect(() => {
    if (tools.length > 0 && onDataExport) {
      onDataExport("toolPerformance", "Tool Performance Summary", tools);
    }
  }, [tools, onDataExport]);

  const fmtLatency = (ms: number, hasCalls: boolean) => {
    if (ms === 0 && !hasCalls) return "--";
    return ms < 1000 ? `${ms.toFixed(2)}ms` : `${(ms / 1000).toFixed(2)}s`;
  };

  const handleDownloadCSV = () => {
    const csvData = tools.map((tool: any) => ({
      "Tool Name": tool.name,
      "Agent Name": tool.agent,
      "Total Calls": tool.count,
      "Successful Calls": tool.count - tool.failed,
      "Failed Calls": tool.failed,
      "Success Rate (%)": tool.successRate.toFixed(2),
      "Average Latency (ms)": tool.avgLatency.toFixed(2),
    }));
    downloadCSV(csvData, `tool_performance_summary_${Date.now()}.csv`);
  };

  return (
    <div css={tableContainerCSS}>
      <div css={tableHeaderCSS}>
        <div css={titleCSS}>
          <span>🛠️</span> Tool Performance Summary
        </div>
        <button css={buttonCSS} onClick={handleDownloadCSV}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download CSV
        </button>
      </div>
      <div css={scrollableWrapperCSS}>
        <table css={tableCSS}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--ac-global-background-color-white)', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <tr>
              <th css={thCSS}>Tool</th>
              <th css={thCSS}>Agent</th>
              <th css={thCSS} style={{ textAlign: 'right' }}>Calls</th>
              <th css={thCSS} style={{ textAlign: 'center' }}>Success</th>
              <th css={thCSS} style={{ textAlign: 'right' }}>Latency (AVG)</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((tool: any, i: number) => (
              <tr key={tool.key} css={toolRowCSS}>
                <td css={tdCSS}>
                  <span css={dotCSS(COLORS[i % COLORS.length])} />
                  <span style={{ fontWeight: 500 }}>{tool.name}</span>
                </td>
                <td css={tdCSS}>
                  <span style={{
                    fontSize: '11px',
                    background: 'var(--ac-global-color-grey-100)',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    color: 'var(--ac-global-text-color-700)',
                    fontWeight: 600
                  }}>
                    {tool.agent}
                  </span>
                </td>
                <td css={tdCSS} style={{ textAlign: 'right' }}>
                  <span css={valueCSS}>{tool.count.toLocaleString()}</span>
                </td>
                <td css={tdCSS} style={{ textAlign: 'center' }}>
                  <span css={css`
                    color: ${tool.successRate >= 95 ? "#10b981" : tool.successRate >= 80 ? "#f59e0b" : "#f43f5e"};
                    font-weight: 600;
                    font-size: 12px;
                    background: ${tool.successRate >= 95 ? "#10b98115" : tool.successRate >= 80 ? "#f59e0b15" : "#f43f5e15"};
                    padding: 4px 8px;
                    border-radius: 6px;
                  `}>
                    {tool.successRate.toFixed(2)}%
                  </span>
                </td>
                <td css={tdCSS} style={{ textAlign: 'right' }}>
                  <span css={valueCSS}>{fmtLatency(tool.avgLatency, tool.count > 0)}</span>
                </td>
              </tr>
            ))}
            {tools.length === 0 && (
              <tr>
                <td colSpan={5} css={tdCSS} style={{ textAlign: 'center', padding: '40px', color: 'var(--ac-global-text-color-400)' }}>
                  No tool calls found for the selected time range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
